/** Birthday rules, from the designer's note on the Birthday section.
 *
 * Verbatim: "2s confetti fall. If there's a birthday reward show it first in available
 * rewards. […] Birthday week starts on day of birthday and lasts a week exactly. (if
 * it's the day of the birthday, show as attached)"
 *
 * Two distinct windows, and conflating them is the easy mistake: the confetti fires on
 * the DAY, the reward stays claimable for the WEEK. A customer who comes in on day
 * three should still get their reward and should not get a party.
 */

import type { RewardOption } from '../../components/v3/RewardCard'

function startOfDay(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime()
}

const DAY_MS = 86_400_000

/** The birthday as it falls in the year of `now` — the stored year is the year of
 * birth and is never the year we are comparing against. */
function thisYearsBirthday(birthday: Date, now: Date): Date {
  return new Date(now.getFullYear(), birthday.getMonth(), birthday.getDate())
}

export function isBirthdayToday(birthday: Date | null | undefined, now: Date = new Date()): boolean {
  if (!birthday) return false
  return startOfDay(thisYearsBirthday(birthday, now)) === startOfDay(now)
}

/** "Starts on day of birthday and lasts a week exactly" — the day itself plus six, so
 * seven days in total. Day eight is over. */
export function isBirthdayWeek(birthday: Date | null | undefined, now: Date = new Date()): boolean {
  if (!birthday) return false
  const start = startOfDay(thisYearsBirthday(birthday, now))
  const elapsed = startOfDay(now) - start
  if (elapsed >= 0) return elapsed < 7 * DAY_MS

  // A birthday in late December, viewed in early January: last year's occurrence may
  // still be inside its week. Without this, someone born on 30 December loses their
  // reward the moment the year ticks over.
  const lastYear = new Date(now.getFullYear() - 1, birthday.getMonth(), birthday.getDate())
  const sinceLastYear = startOfDay(now) - startOfDay(lastYear)
  return sinceLastYear >= 0 && sinceLastYear < 7 * DAY_MS
}

/** Birthday reward first, everything else in the order the server sent it.
 *
 * A stable sort on one key only: the server already ordered the catalogue, and
 * re-sorting it here would quietly override a merchant's display order.
 */
export function withBirthdayFirst(rewards: RewardOption[]): RewardOption[] {
  const birthday = rewards.filter((reward) => reward.birthday)
  if (birthday.length === 0) return rewards
  return [...birthday, ...rewards.filter((reward) => !reward.birthday)]
}
